import { Component, createSignal, onMount, Show, createEffect, createMemo, onCleanup } from 'solid-js';
import { Toaster, showToast } from './services/toast';
import type { MLCEngine } from '@mlc-ai/web-llm';
import { Header } from './components/Header';
import { SessionsBar, type SessionItem } from './components/SessionsBar';
import { ChatArea } from './components/ChatArea';
import { InputArea } from './components/InputArea';
import { SettingsPanel } from './components/SettingsPanel';
import { AgentsPanel } from './components/AgentsPanel';
import type { PanelConfig, AgentConfig } from './types/agents';
import { StatusBar } from './components/StatusBar';
import { GoogleSearchModule } from './services/googleSearch';
import { ContextPlugin } from './services/contextPlugin';
import { getModelFromCache, clearModelCache, getModelCacheSize } from './services/modelCache';
import type { Message } from './types/chat';

// WebLLM stream response types
interface StreamChoice {
  delta?: {
    content?: string;
    role?: string;
  };
  index: number;
  finish_reason?: string | null;
}

interface StreamResponse {
  choices: StreamChoice[];
  created: number;
  id: string;
  model: string;
  object: string;
}

const App: Component = () => {
  // State management
  const [messages, setMessages] = createSignal<Message[]>([]);
  const [status, setStatus] = createSignal('Initializing WebLLM...');
  const [isLoading, setIsLoading] = createSignal(false);
  const [selectedModel, setSelectedModel] = createSignal('');
  const [modelList, setModelList] = createSignal<string[]>([]);
  const [showSettings, setShowSettings] = createSignal(false);
  const [webSearchEnabled, setWebSearchEnabled] = createSignal(false);
  const [inputText, setInputText] = createSignal('');
  const [isStreaming, setIsStreaming] = createSignal(false);
  const [loadingProgress, setLoadingProgress] = createSignal(0);
  const [connectionStatus, setConnectionStatus] = createSignal<'connected' | 'disconnected' | 'connecting'>('disconnected');
  const [engine, setEngine] = createSignal<MLCEngine | null>(null);
  const [cacheCount, setCacheCount] = createSignal<number>(0);
  const [lastLatencyMs, setLastLatencyMs] = createSignal(0);
  const [lastCps, setLastCps] = createSignal(0);
  // UI: sessions drawer
  const [showSessions, setShowSessions] = createSignal(false);
  // Sampling configuration (global)
  const [temperature, setTemperature] = createSignal<number>(0.7);
  const [maxTokens, setMaxTokens] = createSignal<number | undefined>(undefined);
  const [topP, setTopP] = createSignal<number | undefined>(undefined);
  const [presencePenalty, setPresencePenalty] = createSignal<number | undefined>(undefined);
  const [frequencyPenalty, setFrequencyPenalty] = createSignal<number | undefined>(undefined);
  // UI prefs for thinking display
  const [showThinkingDefault, setShowThinkingDefault] = createSignal<boolean>(false);
  const [expandThinkingWhileStreaming, setExpandThinkingWhileStreaming] = createSignal<boolean>(true);
  // Search behavior preference
  const [searchMode, setSearchMode] = createSignal<'auto' | 'always' | 'never'>('auto');
  // System prompt
  const [systemPrompt, setSystemPrompt] = createSignal<string>('');
  // Agents panel configuration
  const [panel, setPanel] = createSignal<PanelConfig>({ agents: [], roundRobin: true, maxRounds: 2, shareKnowledgeAcrossAgents: false, dynamicOrchestration: true });
  // Sessions
  type Session = { id: string; name: string; updatedAt: number; messages: Message[]; modelId?: string };
  const [sessions, setSessions] = createSignal<Session[]>([]);
  const [currentSessionId, setCurrentSessionId] = createSignal<string>('');
  
  // Stream control
  let currentAbortController: AbortController | null = null;
  
  // Message queue
  const [messageQueue, setMessageQueue] = createSignal<string[]>([]);
  
  // Services
  let searchModule: GoogleSearchModule | null = null;
  let contextPlugin: ContextPlugin = new ContextPlugin(10);

  // Drawer refs and focus management
  let sessionsDrawer!: HTMLElement;
  let settingsDrawer!: HTMLElement;
  let lastFocusBeforeSessions: HTMLElement | null = null;
  let lastFocusBeforeSettings: HTMLElement | null = null;
  let appMain!: HTMLDivElement;

  const getFocusableElements = (root: HTMLElement): HTMLElement[] => {
    const selectors = [
      'a[href]','button:not([disabled])','textarea:not([disabled])','input:not([disabled])','select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])','[contenteditable="true"]'
    ];
    return Array.from(root.querySelectorAll<HTMLElement>(selectors.join(',')))
      .filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null);
  };
  const focusFirstIn = (root: HTMLElement) => {
    const els = getFocusableElements(root);
    (els[0] || root).focus();
  };
  const trapKeyDown = (e: KeyboardEvent, root: HTMLElement) => {
    if (e.key !== 'Tab') return;
    const els = getFocusableElements(root);
    if (els.length === 0) return;
    const first = els[0];
    const last = els[els.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !root.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Approximate token count for current context (very rough: chars/4)
  // Theme mode: 'dark' | 'light' | 'system'
  const [themeMode, setThemeMode] = createSignal<'dark' | 'light' | 'system'>('system');
  const applyTheme = (mode: 'dark' | 'light' | 'system') => {
    const root = document.documentElement;
    let effective: 'dark' | 'light' = 'dark';
    if (mode === 'system') {
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      effective = prefersDark ? 'dark' : 'light';
    } else {
      effective = mode;
    }
    root.setAttribute('data-theme', effective);
  };
  const tokensEstimate = createMemo(() => {
    const all = messages();
    if (!all || all.length === 0) return 0;
    const totalChars = all.reduce((sum, m) => sum + (m.content?.length || 0), 0);
    return Math.max(0, Math.round(totalChars / 4));
  });
  
  // Initialize on mount
  onMount(async () => {
    // Load available models
  const { prebuiltAppConfig } = await import('@mlc-ai/web-llm');
  const models = prebuiltAppConfig.model_list.map(m => m.model_id);
    setModelList(models);
    
  // Load saved web search settings
    const savedSettings = localStorage.getItem('webSearchSettings');
    if (savedSettings) {
      const { googleApiKey, googleCseId } = JSON.parse(savedSettings);
      if (googleApiKey && googleCseId) {
        searchModule = new GoogleSearchModule(googleApiKey, googleCseId);
        setWebSearchEnabled(true);
      }
    }
    // Load persisted webSearch toggle preference
    try {
      const persistedWeb = localStorage.getItem('webSearchEnabled');
      if (persistedWeb != null) setWebSearchEnabled(persistedWeb === 'true');
    } catch {}
    
    // Load multi-session state
    try {
      const raw = localStorage.getItem('sessions');
      if (raw) {
        const parsed: Session[] = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setSessions(parsed);
          const savedCurrent = localStorage.getItem('currentSessionId') || parsed[0].id;
          setCurrentSessionId(savedCurrent);
          const active = parsed.find(s => s.id === savedCurrent) || parsed[0];
          setMessages(active.messages || []);
          contextPlugin.clearContext();
          (active.messages || []).forEach(m => contextPlugin.addEntry(m.role, m.content));
          if (active.modelId) setSelectedModel(active.modelId);
          setStatus(`Loaded ${parsed.length} conversation${parsed.length>1?'s':''}`);
        }
      }
    } catch {}

    // Back-compat: Try to restore single conversation backup into a new session
    try {
      const conversationBackup = localStorage.getItem('conversationBackup');
      if (conversationBackup) {
        const { messages: savedMessages, modelId } = JSON.parse(conversationBackup);
        if (savedMessages && Array.isArray(savedMessages) && savedMessages.length > 0) {
          const shouldRestore = confirm(
            `Found a previous conversation with ${savedMessages.length} messages. Would you like to restore it?`
          );
          if (shouldRestore) {
            const id = `sess-${Date.now()}`;
            const sess: Session = { id, name: 'Restored chat', updatedAt: Date.now(), messages: savedMessages, modelId };
            setSessions([sess]);
            setCurrentSessionId(id);
            setMessages(savedMessages);
            contextPlugin.clearContext();
            savedMessages.forEach(msg => contextPlugin.addEntry(msg.role, msg.content));
            setStatus(`Restored conversation with ${savedMessages.length} messages`);
            if (modelId && models.includes(modelId)) setSelectedModel(modelId);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to restore conversation backup:', error);
    }

    // Auto-load last used model
    const lastUsedModel = localStorage.getItem('lastUsedModel');
    if (lastUsedModel && models.includes(lastUsedModel)) {
      setSelectedModel(lastUsedModel);
      if (messages().length === 0) {
        await loadModel(lastUsedModel);
      }
    } else {
      // Auto-select and load a sensible default (first available) for hassle-free start
      if (models && models.length > 0) {
        const defaultModel = models[0];
        setSelectedModel(defaultModel);
        await loadModel(defaultModel);
      } else {
        setStatus('Select a model to start chatting');
      }
    }

    // Load cache count for settings
    try {
      const count = await getModelCacheSize();
      setCacheCount(count);
    } catch {}

    // Load UI preferences
    try {
      const prefsRaw = localStorage.getItem('uiPreferences');
      if (prefsRaw) {
        const prefs = JSON.parse(prefsRaw);
        if (typeof prefs.showThinkingDefault === 'boolean') setShowThinkingDefault(prefs.showThinkingDefault);
        if (typeof prefs.expandThinkingWhileStreaming === 'boolean') setExpandThinkingWhileStreaming(prefs.expandThinkingWhileStreaming);
        if (prefs.searchMode === 'auto' || prefs.searchMode === 'always' || prefs.searchMode === 'never') {
          setSearchMode(prefs.searchMode);
        }
      }
    } catch {}

    // Load sampling config and system prompt
    try {
      const sc = localStorage.getItem('samplingConfig');
      if (sc) {
        const cfg = JSON.parse(sc);
        if (typeof cfg.temperature === 'number') setTemperature(cfg.temperature);
        if (typeof cfg.maxTokens === 'number') setMaxTokens(cfg.maxTokens);
        if (typeof cfg.topP === 'number') setTopP(cfg.topP);
        if (typeof cfg.presencePenalty === 'number') setPresencePenalty(cfg.presencePenalty);
        if (typeof cfg.frequencyPenalty === 'number') setFrequencyPenalty(cfg.frequencyPenalty);
      }
    } catch {}
    try {
      const sp = localStorage.getItem('systemPrompt');
      if (typeof sp === 'string') setSystemPrompt(sp);
    } catch {}
    // Helper: default 5-agent optimized team
    const defaultOrchestrator: PanelConfig = {
      roundRobin: true,
      maxRounds: 2,
      shareKnowledgeAcrossAgents: true,
      summarizerEnabled: true,
      summarizerPrompt: 'Be concise, list key decisions, unresolved questions, and next actions.',
      dynamicOrchestration: true,
      agents: [
        { id: 'agent-orchestrator', name: 'Orchestrator', discipline: 'Architect', systemPrompt: 'You are an expert orchestrator. Your job is to analyze the user\'s request and propose the smallest, most effective panel of experts needed to solve it. Return a structured plan with agent roles and brief instructions for each. Consider if web search or external knowledge is needed.', temperature: 0.4, knowledge: [], enabled: true, origin: 'manual', provider: 'webllm' }
      ]
    };

    // Migration: ensure an Orchestrator agent exists for all users without requiring a reset
    const migratePanel = (p: PanelConfig | null | undefined): PanelConfig => {
      // If nothing saved, start with the orchestrator-only default
      if (!p || !Array.isArray(p.agents)) return defaultOrchestrator;
      const hasOrchestrator = p.agents.some(a => a.id === 'agent-orchestrator' || /^(orchestrator)$/i.test(a.name));
      const next: PanelConfig = { dynamicOrchestration: p.dynamicOrchestration ?? true, ...p };
      if (!hasOrchestrator) {
        // Prepend an orchestrator; inherit provider/model from the first existing agent if present
        const first = p.agents[0];
        const orchestrator: AgentConfig = {
          id: 'agent-orchestrator',
          name: 'Orchestrator',
          discipline: 'Architect',
          systemPrompt: 'You are an expert orchestrator. Your job is to analyze the user\'s request and propose the smallest, most effective panel of experts needed to solve it. Return a structured plan with agent roles and brief instructions for each. Consider if web search or external knowledge is needed.',
          temperature: 0.4,
          knowledge: [],
          enabled: true,
          origin: 'manual',
          provider: first?.provider || 'webllm',
          modelId: first?.modelId,
          endpoint: first?.endpoint
        };
        next.agents = [orchestrator, ...p.agents];
      }
      return next;
    };

    // Load agents panel config (defaults to 5-agent optimized team)
    try {
      const raw = localStorage.getItem('agentsPanel');
      if (raw) {
        const parsed = JSON.parse(raw) as PanelConfig;
        if (parsed && Array.isArray(parsed.agents)) setPanel(migratePanel(parsed));
      } else {
        setPanel(defaultOrchestrator);
      }
    } catch {}

    // Theme preference
    try {
      const storedTheme = localStorage.getItem('themeMode');
      if (storedTheme === 'dark' || storedTheme === 'light' || storedTheme === 'system') {
        setThemeMode(storedTheme);
      }
    } catch {}
    // Apply theme and watch system changes if needed
    applyTheme(themeMode());
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onSchemeChange = () => { if (themeMode() === 'system') applyTheme('system'); };
    mql.addEventListener?.('change', onSchemeChange);
    onCleanup(() => mql.removeEventListener?.('change', onSchemeChange));

    // Restore drawer open state
    try {
      const raw = localStorage.getItem('uiDrawers');
      if (raw) {
        const parsed = JSON.parse(raw) as { showSessions?: boolean; showSettings?: boolean };
        if (typeof parsed.showSessions === 'boolean') setShowSessions(parsed.showSessions);
        if (typeof parsed.showSettings === 'boolean') setShowSettings(parsed.showSettings);
      }
    } catch {}

    // Global shortcuts: Ctrl+, for settings; Ctrl+B for sessions; Esc closes drawers
    const onKey = (e: KeyboardEvent) => {
      // Ignore when typing in inputs/textareas
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || (e.target as HTMLElement | null)?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key === ',') {
        e.preventDefault();
        setShowSettings(v => !v);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B') && !typing) {
        e.preventDefault();
        setShowSessions(v => !v);
        return;
      }
      if (e.key === 'Escape') {
        if (showSettings()) { setShowSettings(false); }
        if (showSessions()) { setShowSessions(false); }
      }
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  // Persist drawer open state
  createEffect(() => {
    try { localStorage.setItem('uiDrawers', JSON.stringify({ showSessions: showSessions(), showSettings: showSettings() })); } catch {}
  });

  // Focus trap when drawers open/close + restore focus to opener
  createEffect(() => {
    if (showSessions()) {
      lastFocusBeforeSessions = (document.activeElement as HTMLElement) || null;
      // Allow time for drawer to mount
      setTimeout(() => { if (sessionsDrawer) focusFirstIn(sessionsDrawer); }, 0);
    } else if (lastFocusBeforeSessions) {
      try { lastFocusBeforeSessions.focus(); } catch {}
      lastFocusBeforeSessions = null;
    }
  });
  createEffect(() => {
    if (showSettings()) {
      lastFocusBeforeSettings = (document.activeElement as HTMLElement) || null;
      setTimeout(() => { if (settingsDrawer) focusFirstIn(settingsDrawer); }, 0);
    } else if (lastFocusBeforeSettings) {
      try { lastFocusBeforeSettings.focus(); } catch {}
      lastFocusBeforeSettings = null;
    }
  });

  // Inert main content and lock background scroll when any drawer is open
  createEffect(() => {
    const anyOpen = showSessions() || showSettings();
    try {
      if (appMain) {
        if (anyOpen) {
          appMain.setAttribute('aria-hidden', 'true');
          // inert may not be typed; set via setAttribute
          appMain.setAttribute('inert', '');
          appMain.setAttribute('data-inert', 'true');
        } else {
          appMain.removeAttribute('aria-hidden');
          appMain.removeAttribute('inert');
          appMain.removeAttribute('data-inert');
        }
      }
      document.body.classList.toggle('drawer-open', anyOpen);
      (document.body.style as any).overflow = anyOpen ? 'hidden' : '';
    } catch {}
  });

  // Persist agents panel configuration
  createEffect(() => {
    try { localStorage.setItem('agentsPanel', JSON.stringify(panel())); } catch {}
  });
  
  // Persist agents panel configuration
  createEffect(() => {
    try {
      localStorage.setItem('agentsPanel', JSON.stringify(panel()));
    } catch {}
  });
  
  // Load model function
  const loadModel = async (modelId?: string) => {
    const model = modelId || selectedModel();
    if (!model) {
      setStatus('Please select a model');
      return;
    }
    
    setIsLoading(true);
    setConnectionStatus('connecting');
    setLoadingProgress(0);
    setStatus(`Initializing ${model}...`);
    
    try {
      // Check cache first
      const cachedModel = await getModelFromCache(model);
      
      if (cachedModel) {
        setStatus(`Loading ${model} from cache...`);
  const { CreateMLCEngine } = await import('@mlc-ai/web-llm');
  setEngine(await CreateMLCEngine(model, {
          modelCache: cachedModel.data,
          initProgressCallback: (progress) => {
            const percentage = (progress.progress * 100);
            setLoadingProgress(percentage);
            setStatus(`Loading from cache: ${percentage.toFixed(1)}%`);
            
            // Update connection status based on progress
            if (percentage < 100) {
              setConnectionStatus('connecting');
            }
          }
        }));
      } else {
        setStatus(`Downloading ${model}...`);
  const { CreateMLCEngine } = await import('@mlc-ai/web-llm');
  setEngine(await CreateMLCEngine(model, {
          initProgressCallback: (progress) => {
            const percentage = (progress.progress * 100);
            setLoadingProgress(percentage);
            
            if (progress.text) {
              setStatus(`${progress.text}: ${percentage.toFixed(1)}%`);
            } else {
              setStatus(`Downloading model: ${percentage.toFixed(1)}%`);
            }
            
            // Update connection status
            if (percentage < 100) {
              setConnectionStatus('connecting');
            }
          }
        }));
        
        // Note: Model caching is handled automatically by WebLLM
      }
      
  setConnectionStatus('connected');
  setLoadingProgress(100);
  setStatus(`✅ ${model} loaded successfully! Ready to chat.`);
      localStorage.setItem('lastUsedModel', model);
      
  // Add system message (include current date for time-aware reasoning)
  const today = new Date();
  const dateStr = today.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const baseSystem = `You are a helpful AI assistant. Today is ${dateStr}. If web search results are provided, prefer current, sourced answers; otherwise answer from your knowledge.`;
  const custom = systemPrompt().trim();
  contextPlugin.setSystemMessage(custom ? custom : baseSystem);
  // Persist selected model to current session
  setSessions(prev => prev.map(s => s.id === currentSessionId() ? { ...s, modelId: model } : s));
      
    } catch (error) {
      setConnectionStatus('disconnected');
      setLoadingProgress(0);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setStatus(`❌ Failed to load ${model}: ${errorMessage}`);
      console.error('Model loading error:', error);
      
      // Provide helpful error messages based on common issues
      if (errorMessage.includes('network')) {
        setStatus(`❌ Network error: Check your internet connection and try again`);
      } else if (errorMessage.includes('memory') || errorMessage.includes('OOM')) {
        setStatus(`❌ Not enough memory: Try a smaller model or close other browser tabs`);
      } else if (errorMessage.includes('unsupported')) {
        setStatus(`❌ Browser not supported: Try using Chrome, Edge, or Firefox`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to build base system message (global + date)
  const getBaseSystemMessage = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const base = `You are a helpful multi-agent assistant. Today is ${dateStr}. Prior assistant messages may be prefixed with [AgentName]. Read them to coordinate and avoid repetition. If web search results are provided, prefer current, sourced answers; otherwise answer from your knowledge.`;
    const custom = systemPrompt().trim();
    return custom ? `${custom}` : base;
  };

  // Multi-agent collaboration
  // Helper: stream from OpenAI-compatible endpoint
  const streamFromOpenAI = async (
    agent: AgentConfig,
    messages: Array<{ role: 'system'|'user'|'assistant'; content: string }>,
    onDelta: (delta: string) => void,
    signal?: AbortSignal
  ) => {
    const ep = agent.endpoint;
    if (!ep || !ep.baseUrl) throw new Error(`Missing endpoint for agent ${agent.name}`);
    const url = `${ep.baseUrl.replace(/\/$/, '')}${(ep.path || '/v1/chat/completions').startsWith('/') ? (ep.path || '/v1/chat/completions') : `/${ep.path}`}`;
    const headers: Record<string,string> = {
      'Content-Type': 'application/json',
      ...(ep.apiKey ? { Authorization: `Bearer ${ep.apiKey}` } : {}),
      ...(ep.headers || {})
    };
    const body = {
      model: ep.model || 'gpt-4o-mini',
      messages,
      temperature: typeof agent.temperature === 'number' ? agent.temperature : temperature(),
      max_tokens: typeof agent.maxTokens === 'number' ? agent.maxTokens : maxTokens(),
      top_p: typeof agent.topP === 'number' ? agent.topP : topP(),
      presence_penalty: presencePenalty(),
      frequency_penalty: frequencyPenalty(),
      stream: true
    };
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal });
    if (!res.ok) {
      const txt = await res.text().catch(()=>'');
      throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
    }
    const reader = res.body?.getReader();
    if (!reader) return;
    const decoder = new TextDecoder('utf-8');
    let buf = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split('\n');
      buf = parts.pop() || '';
      for (const line of parts) {
        const s = line.trim();
        if (!s) continue;
        if (!s.startsWith('data:')) continue;
        const data = s.slice(5).trim();
        if (data === '[DONE]') return;
        try {
          const json = JSON.parse(data);
          const delta: string = json?.choices?.[0]?.delta?.content || '';
          if (delta) onDelta(delta);
        } catch {}
      }
    }
    if (buf.trim().startsWith('data:')) {
      try {
        const json = JSON.parse(buf.trim().slice(5).trim());
        const delta: string = json?.choices?.[0]?.delta?.content || '';
        if (delta) onDelta(delta);
      } catch {}
    }
  };

  const runAgents = async (userText: string, searchContext: string, agentsOverride?: AgentConfig[]) => {
  // We'll lazily load WebLLM model per agent if needed; OpenAI endpoints don't need engine
  const enabledAgents = (agentsOverride ?? panel().agents).filter(a => a.enabled);
    if (enabledAgents.length === 0) return;

  // We'll compute history fresh before each agent turn to include latest outputs

    const maxRounds = Math.max(1, Math.min(panel().maxRounds || 1, 5));
    const rounds = panel().roundRobin ? maxRounds : 1;

    currentAbortController = new AbortController();
    setIsStreaming(true);

    try {
  for (let r = 0; r < rounds; r++) {
        for (const agent of enabledAgents) {
          if (currentAbortController?.signal?.aborted) break;

          // Build agent-specific system message
          const base = getBaseSystemMessage();
          const knowledgePool = panel().shareKnowledgeAcrossAgents
            ? enabledAgents.flatMap(a => a.knowledge)
            : agent.knowledge;
          const textSnippets = knowledgePool.filter(k => k.type === 'text').map(k => k.value);
          const urls = knowledgePool.filter(k => k.type === 'url').map(k => k.value);
          const knowledgeText = textSnippets.join('\n\n').slice(0, 6000);
          const knowledgeBlock = knowledgeText
            ? `\n\nKnowledge context (may be partial):\n${knowledgeText}`
            : '';
          const urlsBlock = urls.length ? `\n\nReference URLs:\n${urls.map(u => `- ${u}`).join('\n')}` : '';
          const agentHeader = `You are agent "${agent.name}" acting as a ${agent.discipline}. ${agent.systemPrompt || ''}`;
          const searchBlock = searchContext ? `\n\nWeb search results:\n${searchContext}\n\nPrefer current, sourced answers when relevant.` : '';
          const systemForAgent = `${base}\n\n${agentHeader}${knowledgeBlock}${urlsBlock}${searchBlock}\n\nCurrent task: ${userText}`;

          // Build shared history excluding system, up-to-date
          const history = contextPlugin.getFormattedContext().filter(m => m.role !== 'system');
          const msgs = [
            { role: 'system' as const, content: systemForAgent },
            ...history,
            { role: 'user' as const, content: `As ${agent.name} (${agent.discipline}), read prior messages prefixed with [AgentName] and contribute your best next response. Focus on your specialty and avoid repeating others.` }
          ];

          // Streaming completion per agent
          const msgId = `assistant-${agent.id}-${Date.now()}`;
          const placeholder: Message = {
            role: 'assistant', content: '', timestamp: Date.now(), id: msgId,
            agentId: agent.id, agentName: agent.name, agentDiscipline: agent.discipline,
            sources: searchContext || undefined
          };
          setMessages(prev => [...prev, placeholder]);

          // Choose provider
          const provider = agent.provider || 'webllm';
          let openAIStreamPromise: Promise<void> | null = null;
          let webllmStream: AsyncIterable<StreamResponse> | null = null;
          if (provider === 'openai') {
            // Stream via fetch SSE
            openAIStreamPromise = streamFromOpenAI(agent, msgs, (delta) => {
              // Accumulate below; just push delta into processing loop variables
              handleDelta(delta);
            }, currentAbortController?.signal);
          } else {
            // Ensure correct WebLLM model for this agent
            if (agent.modelId && agent.modelId !== selectedModel()) {
              await loadModel(agent.modelId);
            } else if (!engine()) {
              // No engine loaded: try to load default selected model
              if (selectedModel()) await loadModel(selectedModel());
            }
            const eng = engine();
            if (!eng) throw new Error('WebLLM engine not loaded');
            webllmStream = await eng.chat.completions.create({
              messages: msgs,
              temperature: typeof agent.temperature === 'number' ? agent.temperature : temperature(),
              max_tokens: typeof agent.maxTokens === 'number' ? agent.maxTokens : maxTokens(),
              top_p: typeof agent.topP === 'number' ? agent.topP : topP(),
              presence_penalty: presencePenalty(),
              frequency_penalty: frequencyPenalty(),
              stream: true,
              // @ts-expect-error - signal may not exist in type defs
              signal: currentAbortController.signal
            }) as any;
          }

          let thinkingBuffer = '';
          let answerBuffer = '';
          let inThinking = false;
          let lastUpdateTime = Date.now();
          const startTime = Date.now();
          let firstDeltaAt: number | null = null;

          const handleDelta = (delta: string) => {
            if (!firstDeltaAt && delta) firstDeltaAt = Date.now();
            let remaining = delta;
            while (remaining.length > 0) {
              if (!inThinking) {
                const startIdx = remaining.indexOf('<think>');
                if (startIdx === -1) {
                  answerBuffer += remaining;
                  remaining = '';
                } else {
                  answerBuffer += remaining.slice(0, startIdx);
                  remaining = remaining.slice(startIdx + '<think>'.length);
                  inThinking = true;
                }
              } else {
                const endIdx = remaining.indexOf('</think>');
                if (endIdx === -1) {
                  thinkingBuffer += remaining;
                  remaining = '';
                } else {
                  thinkingBuffer += remaining.slice(0, endIdx);
                  remaining = remaining.slice(endIdx + '</think>'.length);
                  inThinking = false;
                }
              }
            }
            const now = Date.now();
            if (now - lastUpdateTime > 50) {
              setMessages(prev => {
                const msgsMut = [...prev];
                const idx = msgsMut.findIndex(m => m.id === msgId);
                if (idx >= 0) msgsMut[idx] = { ...msgsMut[idx], content: answerBuffer, thinking: thinkingBuffer };
                return msgsMut;
              });
              lastUpdateTime = now;
            }
          };

          if (webllmStream) {
            for await (const chunk of webllmStream as AsyncIterable<StreamResponse>) {
              if (currentAbortController?.signal?.aborted) break;
              const delta = (chunk.choices?.[0]?.delta?.content) || '';
              handleDelta(delta);
            }
          } else if (openAIStreamPromise) {
            await openAIStreamPromise;
          }

          // Finalize this agent message
          setMessages(prev => {
            const msgs2 = [...prev];
            const idx = msgs2.findIndex(m => m.id === msgId);
            if (idx >= 0) {
              msgs2[idx] = { ...msgs2[idx], content: answerBuffer, thinking: thinkingBuffer };
            }
            return msgs2;
          });
          if (firstDeltaAt) setLastLatencyMs(firstDeltaAt - startTime);
          const totalMs = Date.now() - (firstDeltaAt ?? startTime);
          const cps = totalMs > 0 ? answerBuffer.length / (totalMs / 1000) : 0;
          setLastCps(cps);
          // Record with agent attribution so future turns know who said what
          contextPlugin.addEntry('assistant', `[${agent.name}] ${answerBuffer}`);
        }
      }
      // Optional summarizer step
  if (panel().summarizerEnabled) {
        const base = getBaseSystemMessage();
        const systemForSummary = `${base}\n\nYou are a neutral summarizer. Read the latest round of agent responses and produce a concise, actionable synthesis. ${panel().summarizerPrompt || ''}`;
        // Collect the last N assistant messages (from this run), simple heuristic: up to enabledAgents.length
        const recent = [...messages()].reverse().filter(m => m.role === 'assistant').slice(0, enabledAgents.length).reverse();
        const summaryMsgs = [
          { role: 'system' as const, content: systemForSummary },
          ...recent.map(m => ({ role: 'assistant' as const, content: `${m.agentName ? `[${m.agentName}] ` : ''}${m.content}` })),
          { role: 'user' as const, content: 'Produce a concise, actionable synthesis with key points and next steps.' }
        ];
        let content = '';
        const eng = engine();
        if (eng) {
          const response: any = await eng.chat.completions.create({ messages: summaryMsgs, temperature: 0.5, top_p: topP(), max_tokens: maxTokens(), presence_penalty: presencePenalty(), frequency_penalty: frequencyPenalty(),
            // @ts-expect-error - signal may not exist
            signal: currentAbortController?.signal });
          content = response?.choices?.[0]?.message?.content || String(response ?? '');
        } else {
          // Fallback to first OpenAI-configured agent
          const openaiAgent = enabledAgents.find(a => (a.provider || 'webllm') === 'openai');
          if (openaiAgent) {
            let acc = '';
            await streamFromOpenAI(openaiAgent, summaryMsgs as any, (d) => { acc += d; }, currentAbortController?.signal);
            content = acc;
          }
        }
        if (content) {
          setMessages(prev => [...prev, { role: 'assistant', content, timestamp: Date.now(), id: `assistant-summary-${Date.now()}`, agentName: 'Summary', agentId: 'summary', agentDiscipline: 'Generalist' }]);
          contextPlugin.addEntry('assistant', `[Summary] ${content}`);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ Agent run failed: ${errorMessage}`, timestamp: Date.now() }]);
    } finally {
      setIsStreaming(false);
      currentAbortController = null;
    }
  };
  
  // Stop current generation
  const stopGeneration = () => {
    // Signal our stream loop abort
    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
    }
    // Try engine-level cancel if available in WebLLM version
    try {
      const e = engine();
      // Some WebLLM versions expose a cancel or abort API; guard it.
      // @ts-expect-error - feature-detect at runtime
      if (e?.chat?.completions?.cancel) {
        // @ts-expect-error runtime guard
        e.chat.completions.cancel();
      }
      const maybeAny: any = e; // feature-detect abort safely
      if (maybeAny && typeof maybeAny.abort === 'function') {
        maybeAny.abort();
      }
    } catch (err) {
      console.debug('Engine-level cancel not available:', err);
    }
    setIsStreaming(false);
    setConnectionStatus('connected');
  showToast('Generation stopped', 'info');
    
    // Update the last message to indicate it was stopped
    setMessages(prev => {
      const msgs = [...prev];
      if (msgs.length > 0 && msgs[msgs.length - 1].role === 'assistant') {
        msgs[msgs.length - 1].content += '\n\n[Response stopped by user]';
      }
      return msgs;
    });
  };
  
  // Process message queue
  const processQueue = async () => {
    const queue = messageQueue();
    const hasOpenAIEnabled = panel().agents.some(a => a.enabled && (a.provider || 'webllm') === 'openai');
    if (queue.length === 0 || isStreaming() || (!engine() && !hasOpenAIEnabled)) return;
    
    const [nextMessage, ...remainingQueue] = queue;
    setMessageQueue(remainingQueue);
    
    await processMessage(nextMessage);
  };
  
  // Process individual message
  const processMessage = async (text: string) => {
    
    // Create abort controller for this request
    currentAbortController = new AbortController();
    
    setIsStreaming(true);
    setConnectionStatus('connected');
    
    // Add user message with unique ID
    const userMessage: Message = { 
      role: 'user', 
      content: text,
      timestamp: Date.now(),
      id: `user-${Date.now()}`
    };
    setMessages(prev => [...prev, userMessage]);
    contextPlugin.addEntry('user', text);
    
    // Web search if enabled and heuristically beneficial
    let searchContext = '';
    const shouldSearch = (q: string) => {
      const query = q.toLowerCase();
      // Hard blocks: user explicitly says no search
      if (/(no web|no search|don't search|without searching)/i.test(q)) return false;

      // If explicitly asks to search/web/google
      if (/(search|google|web\s?search|find sources|look up)/i.test(q)) return true;

      // Time-sensitive/recency
      const recency = [
        'today','now','current','latest','recent','breaking','this week','this month','this year','update','updates','news','new ',
        '2024','2025','2026','released','release date','announced','announcement'
      ];
      if (recency.some(k=>query.includes(k))) return true;

      // Dynamic data categories
      const dynamic = [
        'price','stock','ticker','market','forecast','weather','score','results','schedule','flight','train','covid','case count',
        'patch notes','changelog','version ','download link','github release','npm version','security vulnerability','cve'
      ];
      if (dynamic.some(k=>query.includes(k))) return true;

      // Specific entity facts often updated
      const entityQ = [/who is /, /who's /, /ceo of /, /head of /, /acquired by /, /owns /];
      if (entityQ.some(r=>r.test(query))) return true;

      // Requests for citations
      if (/(sources|citations|cite|references|links|urls)/.test(query)) return true;

      // Default: no search
      return false;
    };
  // Apply search mode preference
  let doSearch = false;
  const mode = searchMode();
  if (mode === 'always') doSearch = true;
  else if (mode === 'never') doSearch = false;
  else doSearch = shouldSearch(text);

  if (webSearchEnabled() && searchModule && doSearch) {
      try {
        const searchMessage: Message = {
          role: 'system',
          content: '🔍 Searching the web...',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, searchMessage]);
        
        const results = await searchModule.search(text);
        searchContext = searchModule.formatResults(results);
        
        // Update search status message
        setMessages(prev => {
          const msgs = [...prev];
          msgs[msgs.length - 1].content = searchContext;
          return msgs;
        });
        
  } catch (error) {
        console.error('Search error:', error);
      }
    }
    
  // Prepare messages for LLM
    const contextMessages = contextPlugin.getFormattedContext();
    
    // If we have search context, modify the system message to include it
    if (searchContext) {
      const systemMessageIndex = contextMessages.findIndex(msg => msg.role === 'system');
      if (systemMessageIndex >= 0) {
        // Append search context to existing system message
        const existingContent = contextMessages[systemMessageIndex].content;
        contextMessages[systemMessageIndex].content = `${existingContent}\n\nWeb search results:\n${searchContext}\n\nPlease use these search results to provide an accurate and up-to-date response.`;
      } else {
        // If no system message exists, create one with search context at the beginning
        contextMessages.unshift({
          role: 'system' as const,
          content: `You are a helpful AI assistant.\n\nWeb search results:\n${searchContext}\n\nPlease use these search results to provide an accurate and up-to-date response.`
        });
      }
    }
    
    // If agents are configured, run collaboration and return
    if (panel().agents.some(a => a.enabled)) {
      // Dynamic orchestration mode: first enabled agent acts as orchestrator to propose a JIT team
      if (panel().dynamicOrchestration) {
        let dynamicTeam = await orchestrateTeam(text).catch((e) => {
          console.warn('Orchestration failed, falling back to configured agents:', e);
          return null;
        });
        // If orchestration fails or returns empty, use a heuristic mini-team
        if (!dynamicTeam || dynamicTeam.length === 0) {
          dynamicTeam = makeHeuristicTeam(text);
        }
        await runAgents(text, searchContext, dynamicTeam);
        setIsStreaming(false);
        currentAbortController = null;
        setTimeout(() => processQueue(), 50);
        return;
      }
      // Fallback: use configured panel agents
      await runAgents(text, searchContext);
      // Continue queue after agent run
      setIsStreaming(false);
      currentAbortController = null;
      setTimeout(() => processQueue(), 50);
      return;
    }

    // Add assistant message placeholder
    const botMessage: Message = {
      role: 'assistant',
      content: '', // Start with empty content
      timestamp: Date.now(),
      id: `assistant-${Date.now()}`
    };
    setMessages(prev => [...prev, botMessage]);
    
  try {
      // Stream response
      const stream = await engine()!.chat.completions.create({
        messages: contextMessages,
        temperature: temperature(),
        max_tokens: maxTokens(),
        top_p: topP(),
        presence_penalty: presencePenalty(),
        frequency_penalty: frequencyPenalty(),
        stream: true,
        // Some implementations honor abort via a signal property; pass if supported.
        // @ts-expect-error - signal may not exist in type defs
        signal: currentAbortController.signal
      });
      
  let fullResponse = '';
      let thinkingBuffer = '';
      let answerBuffer = '';
      let inThinking = false;
  let lastUpdateTime = Date.now();
  const startTime = Date.now();
  let firstDeltaAt: number | null = null;
      
  for await (const chunk of stream as AsyncIterable<StreamResponse>) {
        // Check if generation was aborted
  if (currentAbortController?.signal?.aborted) {
          break;
        }
        
  const delta = chunk.choices[0]?.delta?.content || '';
  if (!firstDeltaAt && delta) firstDeltaAt = Date.now();
        fullResponse += delta;

        // Split out <think>...</think> content if present; handle nested/partial chunks
        // We maintain a simple state machine based on tag encounters.
        let remaining = delta;
        while (remaining.length > 0) {
          if (!inThinking) {
            const startIdx = remaining.indexOf('<think>');
            if (startIdx === -1) {
              answerBuffer += remaining;
              remaining = '';
            } else {
              // Add content before <think> to answer
              answerBuffer += remaining.slice(0, startIdx);
              remaining = remaining.slice(startIdx + '<think>'.length);
              inThinking = true;
            }
          } else {
            const endIdx = remaining.indexOf('</think>');
            if (endIdx === -1) {
              thinkingBuffer += remaining;
              remaining = '';
            } else {
              thinkingBuffer += remaining.slice(0, endIdx);
              remaining = remaining.slice(endIdx + '</think>'.length);
              inThinking = false;
            }
          }
        }
        
        // Throttle updates for better performance
        const now = Date.now();
  if (now - lastUpdateTime > 50 || !delta) { // Update every 50ms or on completion
      setMessages(prev => {
            const msgs = [...prev];
            if (msgs.length > 0 && msgs[msgs.length - 1].role === 'assistant') {
              // Create a new message object instead of mutating
              msgs[msgs.length - 1] = {
                ...msgs[msgs.length - 1],
                content: answerBuffer,
        thinking: thinkingBuffer,
        // Attach sources if we did a search
        sources: searchContext || msgs[msgs.length - 1].sources
              };
            }
            return msgs;
          });
          lastUpdateTime = now;
        }
      }
      
      // Final update to ensure we have the complete response
      setMessages(prev => {
        const msgs = [...prev];
        if (msgs.length > 0 && msgs[msgs.length - 1].role === 'assistant') {
          // Create a new message object instead of mutating
          msgs[msgs.length - 1] = {
            ...msgs[msgs.length - 1],
            content: answerBuffer,
      thinking: thinkingBuffer,
      sources: searchContext || msgs[msgs.length - 1].sources
          };
        }
        return msgs;
      });
      
  contextPlugin.addEntry('assistant', `[Assistant] ${answerBuffer}`);
  if (firstDeltaAt) setLastLatencyMs(firstDeltaAt - startTime);
  const totalMs = Date.now() - (firstDeltaAt ?? startTime);
  const cps = totalMs > 0 ? answerBuffer.length / (totalMs / 1000) : 0;
  setLastCps(cps);
      
    } catch (error) {
      setConnectionStatus('disconnected');
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      setMessages(prev => {
        const msgs = [...prev];
        if (msgs.length > 0) {
          msgs[msgs.length - 1].content = `❌ Error generating response: ${errorMessage}`;
        }
        return msgs;
      });
      
      console.error('Message generation error:', error);
    } finally {
      setIsStreaming(false);
      currentAbortController = null;
      
      // Continue processing queue
      setTimeout(() => processQueue(), 100);
    }
  };

  // Types and helpers for orchestration
  type OrchestratedAgentPlan = {
    name: string; // e.g., Researcher, Coder, Reviewer, Writer, Analyst, Product
    discipline?: AgentConfig['discipline'];
    systemPrompt?: string; // brief instruction for the role
    temperature?: number;
  };

  const tryParseJsonBlock = (text: string): any | null => {
    // Try to parse JSON from the assistant output; supports fenced code blocks
    const fenceMatch = text.match(/```json\s([\s\S]*?)```/i) || text.match(/```\s*([\s\S]*?)```/i);
    const candidate = fenceMatch ? fenceMatch[1] : text;
    // First attempt direct parse
    try { return JSON.parse(candidate); } catch {}
    // Fallback: extract between first '{' and last '}' and try to balance braces
    const first = candidate.indexOf('{');
    const last = candidate.lastIndexOf('}');
    if (first >= 0 && last > first) {
      const sliced = candidate.slice(first, last + 1);
      try { return JSON.parse(sliced); } catch {}
    }
    return null;
  };

  // Enforce mandatory agents and minimum team size
  const ensureMandatoryTeam = (userText: string, initial: AgentConfig[], orchestratorLike?: AgentConfig): AgentConfig[] => {
    const lower = userText.toLowerCase();
    const byName = (name: string) => initial.some(a => a.name.toLowerCase() === name.toLowerCase());
    const hasDiscipline = (d: AgentConfig['discipline']) => initial.some(a => a.discipline === d);

    const mk = (suffix: string, name: string, discipline: AgentConfig['discipline'], systemPrompt: string, temperature = 0.5): AgentConfig => ({
      id: `jit-${Date.now()}-${suffix}`,
      name,
      discipline,
      systemPrompt,
      temperature,
      knowledge: panel().shareKnowledgeAcrossAgents ? panel().agents.flatMap(a => a.knowledge) : (orchestratorLike?.knowledge || []),
      enabled: true,
      origin: 'orchestrated',
      provider: orchestratorLike?.provider || 'webllm',
      modelId: orchestratorLike?.modelId,
      endpoint: orchestratorLike?.endpoint
    });

    const out: AgentConfig[] = [...initial];

    // 1) Accuracy Checker (always include)
    if (!byName('Accuracy Checker')) {
      out.push(mk('acc', 'Accuracy Checker', 'Reviewer', 'You are an accuracy checker. Cross-verify claims, code, and reasoning. Flag contradictions, missing citations, and unsafe assumptions. Suggest corrections with minimal changes.', 0.2));
    }

    // 2) Formatter (always include)
    if (!byName('Formatter')) {
      out.push(mk('fmt', 'Formatter', 'Writer', 'You are a formatter and clarity optimizer. Rewrite the final response for structure, brevity, and readability. Use consistent headings, bullets, and code fences. Preserve meaning; do not invent facts.', 0.4));
    }

  // 3) Ensure at least two other experts besides Accuracy Checker and Formatter
  const countOthers = () => out.filter(a => a.name !== 'Accuracy Checker' && a.name !== 'Formatter').length;
    const suggestions: AgentConfig[] = [];
    const addIf = (cond: boolean, agent: AgentConfig) => { if (cond) suggestions.push(agent); };

    addIf(/code|implement|build|function|typescript|javascript|react|solid|css|api|compile|bug|fix|refactor/.test(lower),
      mk('coder', 'Coder', 'Coder', 'You are an implementation expert. Produce correct, efficient, and secure code with brief inline comments.'));    
    addIf(/plan|design|architecture|structure|strategy|roadmap/.test(lower) || !hasDiscipline('Architect'),
      mk('planner', 'Planner', 'Architect', 'You are a planner. Outline steps, decisions, risks, and alternatives to guide execution.', 0.5));
    addIf(/research|compare|sources|cite|evidence|news|recent/.test(lower) || !hasDiscipline('Researcher'),
      mk('researcher', 'Researcher', 'Researcher', 'You are a researcher. Gather facts, compare viewpoints, and cite reliable sources succinctly.', 0.3));
    addIf(/write|draft|summarize|explain|document|spec|proposal/.test(lower),
      mk('writer', 'Writer', 'Writer', 'You are a writer. Draft clear, audience-appropriate content incorporating team findings.', 0.6));
    addIf(/analy(s|z)e|metrics|data|tradeoff|benchmark|perf|latency|memory/.test(lower),
      mk('analyst', 'Analyst', 'Analyst', 'You are an analyst. Quantify trade-offs, constraints, and evaluate options objectively.', 0.4));

    for (const cand of suggestions) {
      if (countOthers() >= 2) break; // stop once we have at least two others besides mandatory roles
      if (!out.some(a => a.name === cand.name)) out.push(cand);
    }

    // If still short (edge case), fill with Generalist(s)
    while (countOthers() < 2) {
      out.push(mk('gen', `Generalist ${Date.now() % 1000}`, 'Generalist', 'You are a versatile contributor. Fill gaps, glue insights, and ensure coherence across roles.'));
    }

    // Reorder for maximum effectiveness
    //  - Others first in a priority order by discipline
    //  - Accuracy Checker second-to-last
    //  - Formatter last
    const priority: Record<AgentConfig['discipline'], number> = {
      Architect: 1,
      Researcher: 2,
      Coder: 3,
      Reviewer: 4,
      Analyst: 5,
      Product: 6,
      Writer: 7,
      Generalist: 8
    };
    // Deduplicate by name (case-insensitive), preserve first occurrence
    const seen = new Set<string>();
    const unique = out.filter(a => {
      const key = a.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const acc = unique.find(a => a.name.toLowerCase() === 'accuracy checker');
    const fmt = unique.find(a => a.name.toLowerCase() === 'formatter');
    const others = unique.filter(a => a !== acc && a !== fmt);
    const sortedOthers = others.slice().sort((a, b) => {
      const pa = priority[a.discipline] ?? 99;
      const pb = priority[b.discipline] ?? 99;
      if (pa !== pb) return pa - pb;
      return 0; // stable enough for our purposes
    });
    const finalTeam: AgentConfig[] = [];
    finalTeam.push(...sortedOthers);
    if (acc) finalTeam.push(acc);
    if (fmt) finalTeam.push(fmt);
    return finalTeam;
  };

  const orchestrateTeam = async (userText: string): Promise<AgentConfig[] | null> => {
    // Prefer the explicit Orchestrator agent; fall back to first enabled agent
    const orchestrator = panel().agents.find(a => a.id === 'agent-orchestrator' || /^(orchestrator)$/i.test(a.name))
      || panel().agents.find(a => a.enabled);
    if (!orchestrator) return null;
    // Build a focused system prompt for orchestration
    const base = getBaseSystemMessage();
    const allowed = 'Allowed disciplines: Generalist, Researcher, Architect, Coder, Reviewer, Product, Analyst, Writer';
    const schema = '{ "agents": [ { "name": "Planner", "discipline": "Architect", "systemPrompt": "Short instruction", "temperature": 0.5 } ] }';
    const sys = `${base}\n\nROLE: You are the Orchestrator. Your ONLY job is to plan a minimal, effective team of experts for the user task.\nOUTPUT FORMAT: Return ONLY valid JSON (no markdown, no comments, no prose) matching: ${schema}. ${allowed}.\nCONSTRAINTS: Do NOT solve or answer the task. Do NOT include anything outside JSON. Size the team to the task (1-4 agents). Include at least one agent if the task is non-trivial.`;
    const history = contextPlugin.getFormattedContext().filter(m => m.role !== 'system');
    const msgsBase = [
      { role: 'system' as const, content: sys },
      ...history.slice(-8),
      { role: 'user' as const, content: `Create the expert team plan for this task (return ONLY JSON).\nTask:\n\n\n${'```'}\n${userText}\n${'```'}` }
    ];

    // Prepare streaming capture
    let planText = '';
    const handleDelta = (d: string) => { planText += d; };

    currentAbortController = new AbortController();
    // Choose provider for orchestrator
    if ((orchestrator.provider || 'webllm') === 'openai') {
      await streamFromOpenAI(orchestrator, msgsBase as any, handleDelta, currentAbortController.signal);
    } else {
      if (orchestrator.modelId && orchestrator.modelId !== selectedModel()) {
        await loadModel(orchestrator.modelId);
      } else if (!engine()) {
        if (selectedModel()) await loadModel(selectedModel());
      }
      const eng = engine();
      if (!eng) throw new Error('WebLLM engine not loaded');
      const stream: any = await eng.chat.completions.create({ messages: msgsBase, temperature: orchestrator.temperature ?? 0.3, top_p: orchestrator.topP ?? topP(), max_tokens: Math.min(512, orchestrator.maxTokens ?? (maxTokens() || 512)),
        // @ts-expect-error
        signal: currentAbortController.signal, stream: true });
      for await (const chunk of stream as AsyncIterable<StreamResponse>) {
        const delta = (chunk.choices?.[0]?.delta?.content) || '';
        if (delta) handleDelta(delta);
      }
    }

    // Try to parse the resulting JSON plan
    let parsed = tryParseJsonBlock(planText);
    // If parsing failed, retry once with a correction instruction
    if (!parsed) {
      planText = '';
      const correction = { role: 'user' as const, content: 'Your previous response was not valid JSON. Return ONLY valid JSON for the team plan, no extra text.' };
      const retryMsgs = [...msgsBase, correction];
      if ((orchestrator.provider || 'webllm') === 'openai') {
        await streamFromOpenAI(orchestrator, retryMsgs as any, handleDelta, currentAbortController?.signal);
      } else {
        const eng = engine();
        if (!eng) throw new Error('WebLLM engine not loaded');
        const stream: any = await eng.chat.completions.create({ messages: retryMsgs, temperature: orchestrator.temperature ?? 0.2, top_p: orchestrator.topP ?? topP(), max_tokens: 512,
          // @ts-expect-error
          signal: currentAbortController?.signal, stream: true });
        for await (const chunk of stream as AsyncIterable<StreamResponse>) {
          const delta = (chunk.choices?.[0]?.delta?.content) || '';
          if (delta) handleDelta(delta);
        }
      }
      parsed = tryParseJsonBlock(planText);
    }
    const plans: OrchestratedAgentPlan[] | null = parsed && Array.isArray(parsed.agents) ? parsed.agents : null;
    if (!plans || plans.length === 0) return null;

    // Map plan into AgentConfig objects; inherit provider/model from orchestrator for simplicity
  let jitAgents: AgentConfig[] = plans.map((p, idx) => ({
      id: `jit-${Date.now()}-${idx}`,
      name: p.name || `Agent ${idx + 1}`,
      discipline: (p.discipline as AgentConfig['discipline']) || 'Generalist',
      systemPrompt: p.systemPrompt || `You are ${p.name}. Contribute your best expertise to the task.`,
      temperature: typeof p.temperature === 'number' ? p.temperature : 0.6,
      knowledge: panel().shareKnowledgeAcrossAgents ? panel().agents.flatMap(a => a.knowledge) : (orchestrator.knowledge || []),
      enabled: true,
      origin: 'orchestrated',
      provider: orchestrator.provider || 'webllm',
      modelId: orchestrator.modelId,
      endpoint: orchestrator.endpoint
    }));

  // Enforce mandatory roles and minimum team size
  jitAgents = ensureMandatoryTeam(userText, jitAgents, orchestrator);

    // Update panel to include these orchestrated agents (replacing any previous orchestrated ones)
    setPanel(prev => {
      const baseAgents = (prev.agents || []).filter(a => a.origin !== 'orchestrated');
      return { ...prev, agents: [...baseAgents, ...jitAgents] };
    });

    // Announce the plan to the chat as a system message for visibility
  const planSummary = `Orchestrator selected team: ${jitAgents.map(a => `${a.name} (${a.discipline})`).join(', ')}`;
    setMessages(prev => [...prev, { role: 'system', content: planSummary, timestamp: Date.now(), id: `system-plan-${Date.now()}` }]);
    contextPlugin.addEntry('system', planSummary);
    return jitAgents;
  };

  // Heuristic fallback JIT team when orchestration fails
  const makeHeuristicTeam = (userText: string): AgentConfig[] => {
    const lower = userText.toLowerCase();
  const mk = (id: string, name: string, discipline: AgentConfig['discipline'], systemPrompt: string, temperature = 0.6): AgentConfig => ({ id, name, discipline, systemPrompt, temperature, knowledge: [], enabled: true, origin: 'orchestrated', provider: panel().agents[0]?.provider || 'webllm', modelId: panel().agents[0]?.modelId, endpoint: panel().agents[0]?.endpoint });
    if (/(code|implement|build|function|typescript|javascript|react|solid|css|api|compile|bug|fix|refactor)/.test(lower)) {
      const base = [
        mk(`jit-${Date.now()}-p`, 'Planner', 'Architect', 'Design a clear, step-by-step technical plan with risks and alternatives.', 0.5),
        mk(`jit-${Date.now()}-c`, 'Coder', 'Coder', 'Implement the plan with precise, runnable code and brief explanations.', 0.6),
        mk(`jit-${Date.now()}-r`, 'Reviewer', 'Reviewer', 'Critique the solution: correctness, edge cases, performance, security.', 0.3)
      ];
      return ensureMandatoryTeam(userText, base, panel().agents.find(a => a.id === 'agent-orchestrator'));
    }
    if (/(write|blog|doc|email|explain|summariz|outline|draft|copy)/.test(lower)) {
      const base = [
        mk(`jit-${Date.now()}-w`, 'Writer', 'Writer', 'Produce a clear, audience-appropriate piece with structure and tone.', 0.7),
        mk(`jit-${Date.now()}-rv`, 'Reviewer', 'Reviewer', 'Identify gaps, improve clarity, correctness, and structure.', 0.3)
      ];
      return ensureMandatoryTeam(userText, base, panel().agents.find(a => a.id === 'agent-orchestrator'));
    }
    if (/(research|find|sources|compare|latest|today|current|who is|price|news)/.test(lower)) {
      const base = [
        mk(`jit-${Date.now()}-rs`, 'Researcher', 'Researcher', 'Gather trustworthy, current facts; cite sources succinctly.', 0.4),
        mk(`jit-${Date.now()}-wr`, 'Writer', 'Writer', 'Synthesize into a concise, actionable answer.', 0.6),
        mk(`jit-${Date.now()}-rv2`, 'Reviewer', 'Reviewer', 'Check for accuracy, completeness, and clarity.', 0.3)
      ];
      return ensureMandatoryTeam(userText, base, panel().agents.find(a => a.id === 'agent-orchestrator'));
    }
    const base = [
      mk(`jit-${Date.now()}-pl`, 'Planner', 'Architect', 'Break down the task with steps, assumptions, and risks.', 0.5),
      mk(`jit-${Date.now()}-wr2`, 'Writer', 'Writer', 'Provide a concise, structured final answer.', 0.6)
    ];
    return ensureMandatoryTeam(userText, base, panel().agents.find(a => a.id === 'agent-orchestrator'));
  };
  
  // Send message function (with queuing)
  const sendMessage = async (text: string) => {
    if (!text.trim()) return;
    
    // Add to queue
    setMessageQueue(prev => [...prev, text]);
    
    // Start processing if not already streaming
    if (!isStreaming()) {
      processQueue();
    }
  };

  // Regenerate last assistant reply by re-sending the last user prompt
  const regenerateLast = () => {
    const lastUser = [...messages()].reverse().find(m => m.role === 'user');
    if (!lastUser) return;
    // Optionally remove the last assistant message to avoid clutter
    setMessages(prev => {
      const msgs = [...prev];
      if (msgs.length && msgs[msgs.length - 1].role === 'assistant') {
        msgs.pop();
      }
      return msgs;
    });
    sendMessage(lastUser.content);
  showToast('Regenerating last reply', 'info');
  };

  // Edit last user message then re-send
  const editLastAndResend = (newText: string) => {
    const idx = [...messages()].map((m, i) => ({ m, i })).reverse().find(x => x.m.role === 'user')?.i;
    if (idx === undefined) return;
    // Update messages state
    setMessages(prev => {
      const msgs = [...prev];
      msgs[idx] = { ...msgs[idx], content: newText };
      return msgs;
    });
    // Rebuild context plugin from messages
    contextPlugin.clearContext();
    messages().forEach(m => contextPlugin.addEntry(m.role, m.content));
    setStatus('✏️ Edited last message');
    // Remove trailing assistant to regenerate cleanly
    setMessages(prev => {
      const msgs = [...prev];
      if (msgs.length && msgs[msgs.length - 1].role === 'assistant') {
        msgs.pop();
      }
      return msgs;
    });
    sendMessage(newText);
  showToast('Edited and resent', 'success');
  };
  
  // Save settings function
  const saveSettings = (apiKey: string, cseId: string) => {
    if (!apiKey || !cseId) {
      setStatus('Both API key and CSE ID are required');
      return;
    }
    
    localStorage.setItem('webSearchSettings', JSON.stringify({
      googleApiKey: apiKey,
      googleCseId: cseId
    }));
    
    searchModule = new GoogleSearchModule(apiKey, cseId);
    setWebSearchEnabled(true);
  setStatus('✅ Settings saved!');
  showToast('Settings saved', 'success');
    
    setTimeout(() => setStatus(''), 3000);
  };

  // Import conversation handler
  const importConversation = (msgs: Message[]) => {
    // Basic validation
    const valid = Array.isArray(msgs) && msgs.every(m => m && typeof m.role === 'string' && typeof m.content === 'string');
    if (!valid) {
      setStatus('❌ Invalid conversation file');
      showToast('Invalid conversation file', 'error');
      setTimeout(() => setStatus(''), 3000);
      return;
    }
    // Enrich with timestamps/ids if missing
    const normalized = msgs.map((m, idx) => ({
      role: (m.role as 'user' | 'assistant' | 'system'),
      content: m.content,
      timestamp: m.timestamp || Date.now() - (msgs.length - idx) * 1000,
      id: m.id || `${m.role}-${Date.now()}-${idx}`,
      thinking: (m as any).thinking,
      sources: (m as any).sources
    }));
  setMessages(normalized);
    contextPlugin.clearContext();
    normalized.forEach(msg => contextPlugin.addEntry(msg.role, msg.content));
  setStatus(`✅ Imported ${normalized.length} messages`);
  showToast(`Imported ${normalized.length} messages`, 'success');
    setTimeout(() => setStatus(''), 3000);
  };

  // Settings: clear model cache
  const handleClearCache = async () => {
    await clearModelCache();
    try {
      const count = await getModelCacheSize();
      setCacheCount(count);
    } catch {}
  };

  // Clear conversation function
  const clearConversation = () => {
    if (window.confirm('Are you sure you want to clear the entire conversation? This action cannot be undone.')) {
      setMessages([]);
      contextPlugin.clearContext();
      setStatus('Conversation cleared');
      setTimeout(() => setStatus(''), 2000);
  showToast('Conversation cleared', 'info');
    }
  };

  // Keep current session messages in sync
  createEffect(() => {
    const curId = currentSessionId();
    if (!curId) return;
    const updatedAt = Date.now();
    setSessions(prev => prev.map(s => s.id === curId ? { ...s, messages: messages(), updatedAt, modelId: selectedModel() || s.modelId } : s));
  });
  // Persist sessions and current session id when they change
  createEffect(() => {
    try { localStorage.setItem('sessions', JSON.stringify(sessions())); } catch {}
    try { localStorage.setItem('currentSessionId', currentSessionId()); } catch {}
  });

  // Persist UI preferences
  createEffect(() => {
    try {
      localStorage.setItem('uiPreferences', JSON.stringify({
        showThinkingDefault: showThinkingDefault(),
  expandThinkingWhileStreaming: expandThinkingWhileStreaming(),
  searchMode: searchMode()
      }));
    } catch {}
  });

  // Persist sampling config and system prompt
  createEffect(() => {
    try {
      localStorage.setItem('samplingConfig', JSON.stringify({
        temperature: temperature(),
        maxTokens: maxTokens(),
        topP: topP(),
        presencePenalty: presencePenalty(),
        frequencyPenalty: frequencyPenalty()
      }));
    } catch {}
  });
  createEffect(() => {
    try { localStorage.setItem('systemPrompt', systemPrompt()); } catch {}
  });
  // Apply system prompt to context immediately when changed
  createEffect(() => {
    const sp = systemPrompt().trim();
    if (sp) {
      contextPlugin.setSystemMessage(sp);
    }
  });

  // Session helpers
  const ensureSession = () => {
    if (sessions().length === 0) {
      const id = `sess-${Date.now()}`;
      const s = { id, name: 'New chat', updatedAt: Date.now(), messages: [] as Message[], modelId: selectedModel() };
      setSessions([s]);
      setCurrentSessionId(id);
    }
  };
  const newSession = () => {
    const id = `sess-${Date.now()}`;
    const count = sessions().length + 1;
    const s = { id, name: `Chat ${count}`, updatedAt: Date.now(), messages: [] as Message[], modelId: selectedModel() };
    setSessions(prev => [s, ...prev]);
    setCurrentSessionId(id);
    setMessages([]);
    contextPlugin.clearContext();
  };
  const selectSession = (id: string) => {
    const s = sessions().find(x => x.id === id);
    if (!s) return;
    setCurrentSessionId(id);
    setMessages(s.messages || []);
    contextPlugin.clearContext();
    (s.messages || []).forEach(m => contextPlugin.addEntry(m.role, m.content));
    if (s.modelId) setSelectedModel(s.modelId);
  };
  const renameSession = (id: string, name: string) => {
    setSessions(prev => prev.map(s => s.id === id ? { ...s, name } : s));
  };
  const deleteSession = (id: string) => {
    const remaining = sessions().filter(s => s.id !== id);
    setSessions(remaining);
    if (currentSessionId() === id) {
      if (remaining.length) {
        selectSession(remaining[0].id);
      } else {
        const newId = `sess-${Date.now()}`;
        const s = { id: newId, name: 'New chat', updatedAt: Date.now(), messages: [] as Message[], modelId: selectedModel() };
        setSessions([s]);
        setCurrentSessionId(newId);
        setMessages([]);
        contextPlugin.clearContext();
      }
    }
  };
  const duplicateSession = (id: string) => {
    const s = sessions().find(x => x.id === id);
    if (!s) return;
    const newId = `sess-${Date.now()}`;
    const clone = { ...s, id: newId, name: `${s.name} (copy)`, updatedAt: Date.now() };
    setSessions(prev => [clone, ...prev]);
    selectSession(newId);
  };
  const exportSession = (id: string) => {
    const s = sessions().find(x => x.id === id);
    if (!s) return;
    const data = { name: s.name, updatedAt: s.updatedAt, modelId: s.modelId, messages: s.messages };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${s.name.replace(/\s+/g,'_')}-${new Date().toISOString().replace(/[:.]/g,'-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Ensure at least one session exists
  ensureSession();

  // Persist and apply theme
  createEffect(() => {
    const mode = themeMode();
    try { localStorage.setItem('themeMode', mode); } catch {}
    applyTheme(mode);
  });
  
  return (
    <div class="container">
      <Toaster />
      <Header
        onToggleSettings={() => setShowSettings(!showSettings())}
        onToggleSessions={() => setShowSessions(v => !v)}
        settingsOpen={showSettings()}
      />
  {/* Sessions drawer */}
  <div class="drawer-overlay" data-open={showSessions()} onClick={() => setShowSessions(false)} />
  <aside
    ref={sessionsDrawer}
    class="drawer sessions-drawer"
    data-open={showSessions()}
    aria-label="Sessions drawer"
    tabindex="-1"
    onKeyDown={(e) => trapKeyDown(e as unknown as KeyboardEvent, sessionsDrawer)}
  >
        <div class="drawer-header">
          <h3>Conversations</h3>
          <button type="button" class="icon-btn" aria-label="Close sessions" onClick={() => setShowSessions(false)}>✕</button>
        </div>
        <SessionsBar
          sessions={sessions().map<SessionItem>(s => ({ id: s.id, name: s.name, updatedAt: s.updatedAt }))}
          currentId={currentSessionId()}
          onSelect={(id) => { selectSession(id); setShowSessions(false); }}
          onNew={() => { newSession(); setShowSessions(false); }}
          onRename={renameSession}
          onDelete={deleteSession}
          onDuplicate={duplicateSession}
          onExport={exportSession}
        />
      </aside>
      
      <Show when={showSettings()}>
        <div class="drawer-overlay settings" data-open={showSettings()} onClick={() => setShowSettings(false)} />
        <aside
          ref={settingsDrawer}
          class="drawer drawer-right settings-drawer"
          data-open={showSettings()}
          aria-label="Settings drawer"
          tabindex="-1"
          onKeyDown={(e) => trapKeyDown(e as unknown as KeyboardEvent, settingsDrawer)}
        >
          <div class="drawer-header">
            <h3>Settings</h3>
            <button type="button" class="icon-btn" aria-label="Close settings" onClick={() => setShowSettings(false)}>✕</button>
          </div>
          <div class="drawer-content">
            <SettingsPanel 
              onSave={saveSettings}
              onClose={() => setShowSettings(false)}
              onClearCache={handleClearCache}
              cacheCount={cacheCount()}
              showThinkingDefault={showThinkingDefault()}
              expandThinkingWhileStreaming={expandThinkingWhileStreaming()}
              onToggleShowThinkingDefault={setShowThinkingDefault}
              onToggleExpandThinking={setExpandThinkingWhileStreaming}
              searchMode={searchMode()}
              onChangeSearchMode={setSearchMode}
              systemPrompt={systemPrompt()}
              onSystemPromptChange={setSystemPrompt}
              sampling={{ temperature: temperature(), maxTokens: maxTokens(), topP: topP(), presencePenalty: presencePenalty(), frequencyPenalty: frequencyPenalty() }}
              onSamplingChange={(cfg) => {
                if (typeof cfg.temperature === 'number') setTemperature(cfg.temperature);
                if (typeof cfg.maxTokens !== 'undefined') setMaxTokens(cfg.maxTokens);
                if (typeof cfg.topP !== 'undefined') setTopP(cfg.topP);
                if (typeof cfg.presencePenalty !== 'undefined') setPresencePenalty(cfg.presencePenalty);
                if (typeof cfg.frequencyPenalty !== 'undefined') setFrequencyPenalty(cfg.frequencyPenalty);
              }}
              modelList={modelList()}
              selectedModel={selectedModel()}
              onModelSelect={setSelectedModel}
              onLoadModel={() => loadModel()}
              isLoading={isLoading()}
              themeMode={themeMode()}
              onCycleTheme={() => setThemeMode(prev => prev === 'dark' ? 'light' : prev === 'light' ? 'system' : 'dark')}
            />
            <AgentsPanel panel={panel()} onPanelChange={setPanel} />
          </div>
        </aside>
      </Show>
      
      <div ref={appMain} id="app-main">
        <hr class="modern-separator" />

        <StatusBar 
          status={status()} 
          modelName={selectedModel()}
          isLoading={isLoading()}
          isStreaming={isStreaming()}
          connectionStatus={connectionStatus()}
          loadingProgress={loadingProgress()}
          tokensEstimate={tokensEstimate()}
          latencyMs={lastLatencyMs()}
          cps={lastCps()}
        />

        <ChatArea 
          messages={messages()} 
          isStreaming={isStreaming()}
          onClearConversation={clearConversation}
          showThinkingDefault={showThinkingDefault()}
          expandThinkingWhileStreaming={expandThinkingWhileStreaming()}
          onImportConversation={importConversation}
          onRegenerateLast={regenerateLast}
          onEditLast={editLastAndResend}
        />

        <InputArea
          value={inputText()}
          onChange={setInputText}
          onSend={sendMessage}
          onStop={stopGeneration}
          disabled={(!engine() && !panel().agents.some(a => a.enabled && (a.provider || 'webllm') === 'openai')) || isLoading()}
          webSearchEnabled={webSearchEnabled()}
          webSearchConfigured={!!searchModule}
          onToggleWebSearch={() => {
            const next = !webSearchEnabled();
            setWebSearchEnabled(next);
            try { localStorage.setItem('webSearchEnabled', String(next)); } catch {}
          }}
          isStreaming={isStreaming()}
          queueCount={messageQueue().length}
        />
      </div>
    </div>
  );
};

export default App;